// OtherUsersListsPage.tsx
// 「リスト」パネル。ユーザーページ（UserPage.tsx）の「リスト」タブに埋め込まれる
// 閲覧専用コンポーネント（作成・編集・削除・並び替えなし）。
// 旧 /userlist/:userNameOnURL ページの中身をそのまま移設したもの
// （ページ全体のクロム・プロフィールタイトルは親のUserPage側が持つため、ここでは持たない）。

import React, { useState, useEffect, useMemo } from 'react';
import { fetchPublicLists, fetchListItems } from '../utils/listHandler';
import { getUserSubFromUsernameOpenEndpoint } from '../utils/getUserSubFromUsername';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { useAppTokens } from '../context/ThemeModeContext';
import type { UserList, ListItem, ListOpenLevel } from '../types/rlTypes';

import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemMui from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import LinearProgress from '@mui/material/LinearProgress';
import PublicIcon from '@mui/icons-material/Public';

const openLevelLabel = (level: ListOpenLevel): string => {
  switch (level) {
    case 'public': return '公開';
    case 'url_share': return 'リンク共有';
    case 'friends': return '友達のみ';
    case 'private':
    default: return '非公開';
  }
};

interface OtherUsersListsPanelProps {
  userNameOnURL: string;
  // リスト件数・全リスト内の合計冊数が確定したら呼ばれる（親ページのミニヘッダー統計表示用）
  onStatsLoaded?: (stats: { listCount: number; bookCount: number }) => void;
}

const OtherUsersListsPanel: React.FC<OtherUsersListsPanelProps> = ({ userNameOnURL, onStatsLoaded }) => {
  const TOKENS = useAppTokens();

  const [bookLists, setBookLists] = useState<UserList[]>([]);
  const [itemsByList, setItemsByList] = useState<Record<string, ListItem[]>>({});
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!userNameOnURL) {
        setError('ユーザー名が指定されていません。');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const targetUserSub = await getUserSubFromUsernameOpenEndpoint(userNameOnURL);
        if (!targetUserSub) {
          if (!cancelled) setError('ユーザー情報の取得に失敗しました。');
          return;
        }
        const res = await fetchPublicLists(targetUserSub);
        if (cancelled) return;
        const sorted = (res.items ?? []).slice().sort(
          (a, b) => (a.user_list_order ?? 0) - (b.user_list_order ?? 0),
        );
        setBookLists(sorted);
        if (sorted.length > 0) {
          setSelectedList(sorted[0].user_list_id);
        }
        const totalBooks = sorted.reduce((sum, l) => sum + (l.item_count ?? 0), 0);
        onStatsLoaded?.({ listCount: sorted.length, bookCount: totalBooks });
      } catch (e) {
        if (!cancelled) setError('リストデータの取得中にエラーが発生しました。');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [userNameOnURL, onStatsLoaded]);

  useEffect(() => {
    if (!selectedList || itemsByList[selectedList]) return;
    let cancelled = false;
    setIsLoadingItems(true);
    fetchListItems(selectedList)
      .then((res) => {
        if (cancelled) return;
        setItemsByList((prev) => ({ ...prev, [selectedList]: res.items ?? [] }));
      })
      .catch(() => {
        if (!cancelled) setItemsByList((prev) => ({ ...prev, [selectedList]: [] }));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingItems(false);
      });
    return () => { cancelled = true; };
  }, [selectedList, itemsByList]);

  const sortedSelectedListBooks = useMemo<ListItem[]>(() => {
    if (!selectedList) return [];
    const items = itemsByList[selectedList] ?? [];
    return items.slice().sort((a, b) => {
      const at = a.user_list_item_added_at ? new Date(a.user_list_item_added_at).getTime() : 0;
      const bt = b.user_list_item_added_at ? new Date(b.user_list_item_added_at).getTime() : 0;
      return bt - at;
    });
  }, [itemsByList, selectedList]);

  const handleBookClick = (rlBookId: number) => {
    window.open(`/book/${rlBookId}`, '_blank');
  };

  const selectedListObj = selectedList ? bookLists.find((l) => l.user_list_id === selectedList) : null;

  if (isLoading) {
    return (
      <Box sx={{ py: 4, color: TOKENS.text }}>
        <Typography sx={{ fontSize: 13, color: TOKENS.textSub, mb: 1 }}>
          リストを読み込んでいます...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography sx={{ fontSize: 13, color: TOKENS.error }}>{error}</Typography>
      </Box>
    );
  }

  if (bookLists.length === 0) {
    return (
      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, textAlign: 'center', py: 6 }}>
        公開されているリストがありません。
      </Typography>
    );
  }

  return (
    <Box sx={{ color: TOKENS.text }}>
          <Box
            sx={{
              background: TOKENS.glass,
              border: `1px solid ${TOKENS.border}`,
              borderRadius: 2,
              backdropFilter: 'blur(6px)',
            }}
          >
            {/* header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${TOKENS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                公開しているリストです
              </Typography>
            </Box>

            {/* main split */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  alignItems: 'flex-start',
                  p: 2,
                }}
              >
                {/* 左：リスト一覧 */}
                <Box
                  sx={{
                    width: { xs: '100%', md: 320 },
                    flexShrink: 0,
                    borderRadius: 2,
                    border: `1px solid ${TOKENS.border}`,
                    background: TOKENS.cardBg,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderBottom: `1px solid ${TOKENS.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>リスト</Typography>
                    <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>{bookLists.length}</Typography>
                  </Box>

                  <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
                    <List sx={{ py: 0 }}>
                      {bookLists.map((list) => (
                        <ListItemMui key={list.user_list_id} disablePadding>
                          <ListItemButton
                            selected={selectedList === list.user_list_id}
                            onClick={() => setSelectedList(list.user_list_id)}
                            sx={{
                              py: 1.1,
                              '&.Mui-selected': {
                                background: 'rgba(148,163,184,0.12)',
                              },
                              '&.Mui-selected:hover': {
                                background: 'rgba(148,163,184,0.16)',
                              },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                                    {list.user_list_name}
                                  </Typography>
                                  <PublicIcon sx={{ fontSize: 13, color: TOKENS.textSub }} titleAccess="公開" />
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 0.25 }}>
                                  <Typography component="span" sx={{ fontSize: 12, color: TOKENS.textSub }}>
                                    {`${list.item_count ?? 0} 冊`}
                                  </Typography>
                                  <Typography component="span" sx={{ fontSize: 12, color: TOKENS.textSub }}>
                                    {'  •  '}
                                    {openLevelLabel(list.user_list_open_level)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItemButton>
                        </ListItemMui>
                      ))}
                    </List>
                  </Box>
                </Box>

                {/* 右：本一覧 */}
                <Box
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    borderRadius: 2,
                    border: `1px solid ${TOKENS.border}`,
                    background: TOKENS.cardBg,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderBottom: `1px solid ${TOKENS.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                      {selectedListObj?.user_list_name || '本'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>最新追加順</Typography>
                      {selectedList ? (
                        <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>
                          {sortedSelectedListBooks.length}冊
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  <Box sx={{ px: 1.5, pt: 1.25 }}>
                    {selectedListObj?.user_list_description ? (
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, whiteSpace: 'pre-wrap' }}>
                        {selectedListObj.user_list_description}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                        （説明なし）
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ px: 1.5, py: 1.5 }}>
                    {isLoadingItems && !itemsByList[selectedList ?? ''] ? (
                      <LinearProgress />
                    ) : selectedList ? (
                      <Grid container spacing={1.5} columns={{ xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }}>
                        {sortedSelectedListBooks.map((book) => {
                          const cover = generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13);
                          return (
                            <Grid item xs={1} key={book.rl_book_id}>
                              <Card
                                sx={{
                                  transition: '0.25s',
                                  background: TOKENS.cardBg,
                                  border: `1px solid ${TOKENS.border}`,
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    border: `1px solid ${TOKENS.accent}`,
                                    background: TOKENS.cardBgHover,
                                  },
                                }}
                                onClick={() => handleBookClick(book.rl_book_id)}
                              >
                                <CardMedia
                                  component="img"
                                  sx={{
                                    height: 190,
                                    width: '100%',
                                    objectFit: 'contain',
                                    padding: '10px',
                                    background: TOKENS.cardBg,
                                  }}
                                  image={cover}
                                  alt={book.title ?? ''}
                                />
                              </Card>

                              <Box sx={{ mt: 0.75 }}>
                                <Typography
                                  sx={{
                                    fontSize: 12.5,
                                    color: TOKENS.text,
                                    lineHeight: 1.25,
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {book.title}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: TOKENS.textSub,
                                    mt: 0.25,
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {book.author}
                                </Typography>

                                <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.25 }}>
                                  {book.pub_year ? `${book.pub_year}` : ''}
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>リストを選択してください。</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
          </Box>
    </Box>
  );
};

export default OtherUsersListsPanel;
